import React, { useEffect, useMemo, useRef } from "react";
import { useGiftContext } from "@icoach/gift/components/GiftContext";
import { getInitialPagination } from "@icoach/gift/staticData";
import { OverviewListLayout } from "@icoach/components/overviewList/";
import { formatCurrencyFn } from "@util/utils";
import { MemoControllerBox, OverflowMenu, SelectWithOptions } from "@icoach/components";
import { Grid } from "@mui/material";
import { TextField } from "@curves/ui-base";
import useGiftItemApi from "@apis/useGiftItemApi";
import useMessageDialog from "@util/hook/useMessageDialog";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";

const GiftPage = () => {
	const { dialogRef, setRefresh } = useGiftContext();
	const setMessageDialog = useMessageDialog();
	const { getGiftItemOptionsApi, getGiftItemOverviewApi, putGiftItemEnabledApi } = useGiftItemApi();
	const { options } = useOptions({ getOptionsApi: getGiftItemOptionsApi, isFetchOnMount: true });
	const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
		getOverviewApi: getGiftItemOverviewApi,
		defaultSearchParams: {
			...getInitialPagination(),
			itemName: "",
			giftItemStatus: 99,
		},
		enableInfiniteScroll: true,
		isFetchOnMount: true,
	});

	const giftNameRef = useRef(null);
	const giftStatusRef = useRef(null);

	const getParamsData = () => {
		const result = { ...getInitialPagination() };

		if (giftNameRef.current && giftNameRef.current.getResult) {
			Object.assign(result, { itemName: giftNameRef.current.getResult() });
		}

		if (giftStatusRef.current && giftStatusRef.current.getResult) {
			Object.assign(result, { giftItemStatus: giftStatusRef.current.getResult() });
		}

		return result;
	};

	const handleSubmit = () => {
		const params = getParamsData();
		getOverviewData(params);
	};

	const handlePageChange = (e, page) => {
		updateOverviewPage(page);
	};

	useEffect(
		() => {
			if (typeof setRefresh === "function") {
				setRefresh(() => getOverviewData(searchParams));
			}

			return () => {
				if (typeof setRefresh === "function") setRefresh(null);
			};
		},
		[setRefresh, getOverviewData, searchParams],
	);

	const headerRow = useMemo(
		() => {
			const getOverflowMenuData = (rowData, refresh, dialogRef) => {
				const { giftID, enabled } = rowData || {};
				const toggleLabel = enabled ? "下架" : "上架";
				return [
					{
						text: "編輯贈品",
						onClick: () => {
							dialogRef.current && dialogRef.current.openGiftDialog && dialogRef.current.openGiftDialog(rowData, refresh);
						},
					},
					{
						text: toggleLabel,
						onClick: () => {
							const handleGiftEnabledApi = async (params, refresh, onClose) => {
								const { giftID } = params;
								let resp = await putGiftItemEnabledApi(giftID, params);
								if (resp) {
									if (typeof refresh === "function") refresh(params);
									if (typeof onClose === "function") onClose();
								}
							};

							setMessageDialog({
								open: true,
								title: "提醒",
								msg: `確定要進行${toggleLabel}嗎？`,
								onAsyncConfirm: (_, onclose) =>
									handleGiftEnabledApi(
										{
											giftID,
											enabled: !enabled,
										},
										refresh,
										onclose,
									),
							});
						},
					},
					{
						text: "贈品發送",
						onClick: () => {
							dialogRef.current && dialogRef.current.openGiftDispatchDialog && dialogRef.current.openGiftDispatchDialog({ giftItemID: giftID }, refresh);
						},
					},
				];
			};

			return [
				{
					cellKey: "giftName",
					headerLabel: "贈品名稱",
				},
				{
					cellKey: "enabled",
					headerLabel: "贈品狀態",
					align: "center",
					formatCell: enabled => (enabled ? "上架" : "下架"),
				},
				{
					cellKey: "giftPrice",
					headerLabel: "單價",
					align: "center",
					formatCell: cellValue => formatCurrencyFn(cellValue),
				},
				{
					cellKey: "giftSpecifications",
					align: "center",
					headerLabel: "規格",
				},
                {
                    cellKey: "quantity",
                    headerLabel: "贈品數量",
                    align: "center",
                    formatCell: (cellValue, rowData) => rowData?.isQuantityManaged ? cellValue : '-',
                },
				{
					cellKey: "giftDescription",
					headerLabel: "描述",
					formatCell: cellValue => {
						return <MemoControllerBox data={cellValue} dialogTitle={"商品描述"} />;
					},
				},
				{
					cellKey: "more",
					headerLabel: "",
					width: 6.8,
					formatCell: (value, rowData, { refresh }) => {
						return <OverflowMenu menuData={getOverflowMenuData(rowData, refresh, dialogRef)} />;
					},
				},
			];
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<OverviewListLayout>
			<OverviewListLayout.SearchBar className={"bg-light"} onSubmit={handleSubmit} isShowActions>
				<Grid spacing={2} container>
					<Grid xs={6} item>
						<TextField label={"贈品名稱"} key={searchParams?.itemName} ref={giftNameRef} defaultValue={searchParams?.itemName} InputProps={{ placeholder: "輸入贈品名稱" }} fullWidth />
					</Grid>
					<Grid xs={6} item>
						<SelectWithOptions
							label={"贈品狀態"}
							key={searchParams?.giftItemStatus}
							ref={giftStatusRef}
							defaultValue={searchParams?.giftItemStatus}
							options={options?.giftItemStatusOption}
							fullWidth
						/>
					</Grid>
				</Grid>
			</OverviewListLayout.SearchBar>
			<OverviewListLayout.Summary
				title={"贈品總覽"}
				pageSortAndTotalProps={{
					pageIndex: overviewData?.pagination?.pageIndex,
					pageSize: overviewData?.pagination?.pageSize,
					totalCount: overviewData?.pagination?.totalCount,
					isCurrentPageLastIndex: true,
				}}
			/>
			<OverviewListLayout.Table
				headerRow={headerRow}
				list={overviewData?.list || []}
				bodyRowClass={{
					key: "enabled",
					value: false,
					className: "overview-item-invalid",
				}}
				totalPage={overviewData?.pagination?.totalPage}
				pageIndex={overviewData?.pagination?.pageIndex}
				onChangePage={handlePageChange}
				refresh={() => getOverviewData(searchParams)}
				tableRowBodyProps={{ dialogRef: dialogRef }}
				isMasonryPagination
				isGray
			/>
		</OverviewListLayout>
	);
};

export default GiftPage;
